/**
 * @file Invoice service — quick invoice generation, Stripe checkout
 */

import { store, load } from '@state/store.js';
import { config } from '@core/config.js';

/**
 * Create a quick invoice from a job.
 * @param {Object} job - Job object with line items
 * @returns {Object} Invoice object
 */
export function createQuickInvoice(job) {
  const customer = getCustomerForJob(job);
  const lineItems = (job.lineItems || []).map(item => ({
    description: item.description || item.desc || 'Service',
    quantity: item.quantity || 1,
    unitPrice: item.unitPrice || item.price || 0,
    total: (item.quantity || 1) * (item.unitPrice || item.price || 0),
  }));

  // If no line items, create one from job value
  if (lineItems.length === 0 && job.value) {
    lineItems.push({
      description: job.desc || job.name || 'Handyman Service',
      quantity: 1,
      unitPrice: job.value,
      total: job.value,
    });
  }

  return {
    id: Date.now().toString(36),
    jobId: job.id,
    customerId: job.customerId,
    customerName: customer?.name || customer?.firstName || 'Customer',
    customerEmail: customer?.email || '',
    lineItems,
    subtotal: lineItems.reduce((sum, i) => sum + i.total, 0),
    tax: 0,
    total: lineItems.reduce((sum, i) => sum + i.total, 0),
    status: 'draft',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Get invoice total.
 */
export function getInvoiceTotal(invoice) {
  return (invoice.lineItems || []).reduce((sum, i) => sum + (i.total || 0), 0);
}

/**
 * Create a Stripe checkout session via edge function.
 */
export async function createStripeCheckout(invoice) {
  try {
    const response = await fetch(config.edgeFunctions.stripeInvoice, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'create_checkout',
        invoice,
      }),
    });
    if (!response.ok) return { error: 'Failed to create checkout' };
    const data = await response.json();
    return { url: data.url };
  } catch (e) {
    return { error: e.message };
  }
}

/**
 * Save an invoice to the saved_invoices store.
 */
export function saveInvoice(invoice) {
  const invoices = load('saved_invoices', []);
  const idx = invoices.findIndex(i => i.id === invoice.id);
  if (idx !== -1) {
    invoices[idx] = invoice;
  } else {
    invoices.unshift(invoice);
  }
  store('saved_invoices', invoices);
  return invoice;
}

function getCustomerForJob(job) {
  if (!job?.customerId) return null;
  const customers = load('customers', []);
  return customers.find(c => c.id === job.customerId) || null;
}
